import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Logo } from '@/components/motion/Primitives';
import { getErrorMessage } from '@/lib/format';

export default function Signup() {
    const { signup } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const redirect = params.get('redirect') || '/account';
    const [form, setForm] = useState({ name: '', email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (form.password.length < 6) { toast.error('Password must be at least 6 characters.'); return; }
        setLoading(true);
        try {
            const user = await signup(form.name, form.email, form.password);
            toast.success(`Welcome to xtra, ${user.name}!`);
            navigate(redirect);
        } catch (err) {
            toast.error(getErrorMessage(err, 'Sign up failed.'));
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-[70vh] grid place-items-center px-4 py-16 section-wash">
            <div className="w-full max-w-md rounded-3xl border border-border bg-white p-8 shadow-[0_20px_60px_rgba(2,6,23,0.08)]">
                <div className="flex justify-center"><Logo className="h-9" /></div>
                <h1 className="font-display text-2xl font-bold text-center mt-4">Create your account</h1>
                <p className="text-center text-sm text-[var(--ink-muted)] mt-1">Join the xtra family for faster checkout</p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div><Label htmlFor="name">Full name</Label><Input id="name" data-testid="signup-name-input" required value={form.name} onChange={set('name')} className="mt-1.5 h-11" placeholder="Your name" /></div>
                    <div><Label htmlFor="email">Email</Label><Input id="email" data-testid="signup-email-input" type="email" required value={form.email} onChange={set('email')} className="mt-1.5 h-11" placeholder="you@email.com" /></div>
                    <div><Label htmlFor="password">Password</Label><Input id="password" data-testid="signup-password-input" type="password" required value={form.password} onChange={set('password')} className="mt-1.5 h-11" placeholder="At least 6 characters" /></div>
                    <button data-testid="signup-submit-button" disabled={loading} className="btn-primary w-full h-12 disabled:opacity-80">{loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Create account'}</button>
                </form>
                <p className="mt-5 text-center text-sm text-[var(--ink-muted)]">Already have an account? <Link to="/login" className="text-[var(--brand-blue)] font-semibold hover:underline">Log in</Link></p>
            </div>
        </div>
    );
}
